import { LegacyIcon } from '../LegacyIcon';
import { FC } from 'react';
import { I18NInterface } from '../../../utils';

type Props = {
  rating: number;
  i18n?: I18NInterface<'starRating'>;
};

/**
 * Renders a star rating based on the given rating. The rating is rounded to the nearest 0.5.
 */
const AppStoreRating: FC<Props> = ({ rating, i18n }) => {
  // Calculate the number of full stars and whether we need to add a half star.
  let numOfFullStars = Math.floor(rating);
  let addHalfStar = false;
  const diff = rating - numOfFullStars;

  if (diff > 0.3 && diff <= 0.7) {
    addHalfStar = true;
  } else if (diff > 0.7) {
    numOfFullStars++;
  }

  // Create an array of five stars and render the correct icon for each.
  const stars = [...Array(5)].map((_, idx) => {
    // The current star number is the index + 1.
    // For example, the first star is number 1 and the last star is number 5.
    const currStarNum = idx + 1;

    // If the current star number is less than or equal to the number of full stars, render a full star.
    if (currStarNum <= numOfFullStars) {
      return <LegacyIcon key={idx} name="star" />;
    }

    // If the current star number is equal to the number of full stars + 1 and we need to add a half star, render a half star.
    if (currStarNum === numOfFullStars + 1 && addHalfStar) {
      return <LegacyIcon key={idx} name="star-half" />;
    }

    // Otherwise, render an empty star.
    return <LegacyIcon key={idx} name="star-outline" />;
  });

  return (
    <div
      aria-label={`${numOfFullStars} ${i18n?.starRating ?? 'of 5 stars rated'}`}
      role={'img'}
    >
      <span className="flex" aria-hidden={true}>
        {stars}
      </span>
    </div>
  );
};

export default AppStoreRating;
